<template>
  <div>
    <v-row>
      <v-col md="2">
        <v-text-field
          hide-details="auto"
          type="date"
          solo
          flat
          background-color="grey lighten-4"
          v-model="searchParams.due_date_start"
          label="Data Inicial"
          @change="select(1)"
        />
      </v-col>
      <v-col md="2">
        <v-text-field
          hide-details="auto"
          type="date"
          solo
          flat
          background-color="grey lighten-4"
          v-model="searchParams.due_date_end"
          label="Data Final"
          @change="select(1)"
        />
      </v-col>
      <v-col md="6">
        <company-select
          chips
          multiple
          v-model="searchParams.company_id"
          :item-value="(val) => val.id"
          :itemText="(val) => val.short_name"
          solo
          flat
          background-color="grey lighten-4"
          :dense="false"
          @change="select(1)"
        />
      </v-col>
      <v-col md="2">
        <v-menu
          v-model="columnMenu"
          :close-on-content-click="false"
          min-width="400"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              block
              elevation="0"
              class="pa-0 ma-0"
              style="box-shadow: none !important; min-height: 46px;"
            >
              Colunas
              <v-icon right color="secondary">mdi-view-column</v-icon>
            </v-btn>
          </template>

          <v-card flat>
            <v-card-text
              class="pa-0"
              style="max-height: 400px; overflow-y: auto;"
            >
              <v-list dense>
                <v-list-item-group multiple>
                  <v-list-item
                    v-for="column in columnOptions"
                    :key="column.value"
                    @click="toggleColumn(column.value)"
                    class="px-4"
                  >
                    <v-list-item-action>
                      <v-checkbox
                        :input-value="visibleColumns.includes(column.value)"
                        color="primary"
                        hide-details
                        @click.stop="toggleColumn(column.value)"
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">
                        {{ column.text }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-3">
              <v-btn small color="primary" @click="selectAllColumns">
                <v-icon left small>mdi-check-all</v-icon>
              </v-btn>
              <v-btn small outlined color="error" @click="clearAllColumns">
                <v-icon left small>mdi-close-box-multiple</v-icon>
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn small color="primary" @click="resetDefaultColumns">
                <v-icon left small>mdi-refresh</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>

    <v-row>
      <v-col md="6">
        <app-text-search-field
          @filter="(showFilter = !showFilter), handleClearFilter()"
          label="Pesquisar por produtos"
          @input="handleSearchInput($event)"
          :loading="isLoading"
          :key="textSearchKey"
        />
      </v-col>
      <v-col md="6">
        <ProductSearch
          flat
          solo
          :dense="false"
          :showStock="true"
          @input="setProduct()"
          v-model="search"
          background-color="grey lighten-4"
        />
      </v-col>
    </v-row>

    <v-row v-if="showFilter">
      <v-col md="3">
        <CategoriesSearch
          @input="handleRelationshipInput($event, 'category_id')"
        />
      </v-col>

      <v-col md="3">
        <BrandsSearch @input="handleRelationshipInput($event, 'brand_id')" />
      </v-col>

      <v-col md="3">
        <app-select
          :items="deliveryStatusData"
          v-model="searchParams.delivery_type"
          label="Entrega"
          @input="select(1)"
        />
      </v-col>
      <v-col md="3">
        <OperationSelect
          v-model="searchParams.operation_id"
          @input="select(1)"
        />
      </v-col>

      <v-col md="3">
        <SalesmanSearch
          :hidePosition="true"
          clearable
          @input="setSalesman($event)"
        />
      </v-col>
    </v-row>

    <!-- Cards com métricas -->
    <v-row class="mb-4">
      <v-col md="4">
        <v-card color="primary" elevation="0" dark>
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Total Geral
              <br />
              <b class="text-h5">R$ {{ $format.decimal(grandTotal) }}</b>
              <br />
              <small style="opacity: 0;">
                .
              </small>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="4">
        <v-card color="grey lighten-4" elevation="0">
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Lucro Total
              <br />
              <b class="text-h5">R$ {{ $format.decimal(grandProfit) }}</b>
              <br />
              <small>({{ $format.decimal(grandProfitPercent) }}%)</small>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="4">
        <v-card color="grey lighten-4" elevation="0">
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Lucro Total Projetado
              <br />
              <b class="text-h5">
                R$ {{ $format.decimal(grandProjectedProfit) }}
              </b>
              <br />
              <small>
                ({{ $format.decimal(grandProjectedProfitPercent) }}%)
              </small>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-4">
      <v-col>
        <v-card flat color="grey lighten-5">
          <v-card-text class="py-4 px-5">
            <v-row class="align-center">
              <v-col md="8">
                <div class="d-flex align-center flex-wrap">
                  <span
                    class="text-body-1 font-weight-medium mr-4 text-primary"
                  >
                    Ordenar por:
                  </span>
                  <v-chip-group
                    v-model="searchParams.orderBy"
                    mandatory
                    active-class="primary white--text"
                    @change="select(1)"
                  >
                    <v-chip filter value="product_code" class="mx-1" small>
                      Código
                    </v-chip>
                    <v-chip filter value="product_name" class="mx-1" small>
                      Descrição
                    </v-chip>
                    <v-chip filter value="quantity" class="mx-1" small>
                      Quantidade
                    </v-chip>
                    <v-chip filter value="total" class="mx-1" small>
                      Total
                    </v-chip>
                    <v-chip filter value="sale_abc_curve" class="mx-1" small>
                      ABC Vendas
                    </v-chip>
                  </v-chip-group>
                </div>
              </v-col>
              <v-col md="4">
                <div class="d-flex align-center justify-end flex-wrap">
                  <span
                    class="text-body-1 font-weight-medium mr-3 text-primary"
                  >
                    Ordem:
                  </span>
                  <v-chip-group
                    v-model="searchParams.orderDirection"
                    mandatory
                    active-class="primary white--text"
                    @change="select(1)"
                    class="mr-4"
                  >
                    <v-chip filter value="asc" class="mx-1" small>
                      Crescente
                    </v-chip>
                    <v-chip filter value="desc" class="mx-1" small>
                      Decrescente
                    </v-chip>
                  </v-chip-group>
                  <v-btn
                    color="success"
                    @click="handleExport"
                    small
                    class="px-4"
                  >
                    <v-icon left>mdi-microsoft-excel</v-icon>
                    Exportar
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-6">
      <v-col>
        <v-card flat color="grey lighten-4">
          <v-card-text>
            <v-data-table
              class="grey lighten-4"
              :headers="filteredHeaders"
              :items="sales.data"
              hide-default-footer
              :items-per-page="-1"
              disable-sort
            >
              <template v-slot:item.quantity="{ item }">
                {{ Number(item.quantity).toFixed(0) }}
              </template>

              <template v-slot:item.profit_sum="{ item }">
                R${{ $format.decimal(item.profit_sum) }}
              </template>
              <template v-slot:item.average_price="{ item }">
                R${{ $format.decimal(item.average_price) }}
              </template>

              <template v-slot:item.total="{ item }">
                R${{ $format.decimal(item.total) }}
              </template>
              <template v-slot:item.projected_profit="{ item }">
                R${{ $format.decimal(item.projected_profit) }}
              </template>

              <template v-slot:item.profit_percent="{ item }">
                {{ $format.decimal(item.profit_percent) }}%
              </template>

              <template v-slot:item.projected_profit_percent="{ item }">
                {{ $format.decimal(item.projected_profit_percent) }}%
              </template>

              <template v-slot:item.variant_profit_percent="{ item }">
                {{ $format.decimal(item.variant_profit_percent) }}%
              </template>
            </v-data-table>

            <app-pagination @click="select($event)" :data="sales" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import AccountPlanSelect from "@/components/account-plan/ui/AccountPlanSelect.vue";
import CompanySelect from "@/components/company/ui/CompanySelect.vue";
import OperationSelect from "@/components/operation/ui/OperationSelect.vue";
import SalesmanSearch from "@/components/employees/ui/SalesmanSearch.vue";
import SaleDeliveryStatus from "@/components/sales/sections/SaleDeliveryStatus.vue";
import BrandsSearch from "@/components/brands/ui/BrandsSearch.vue";
import CategoriesSearch from "@/components/categories/ui/CategoriesSearch.vue";
import ProductSearch from "@/components/product/ui/ProductSearch.vue";

import { format, startOfMonth } from "date-fns";

export default {
  components: {
    AccountPlanSelect,
    CompanySelect,
    SaleDeliveryStatus,
    SalesmanSearch,
    OperationSelect,
    CategoriesSearch,
    BrandsSearch,
    ProductSearch,
  },

  data() {
    return {
      textSearchKey: 0,

      headers: [
        { text: "Código", value: "product_code", align: "center" },
        { text: "Descrição", value: "product_name", align: "start" },
        { text: "Marca", value: "brand_name", align: "start" },

        { text: "Lucro atual", value: "variant_profit_percent", align: "end" },

        { text: "Categoria", value: "category_name", align: "start" },
        { text: "Quantidade", value: "quantity", align: "center" },
        { text: "Lucro(R$)", value: "profit_sum", align: "end" },
        { text: "Lucro(%)", value: "profit_percent", align: "end" },
        {
          text: "L.Projetado(R$)",
          value: "projected_profit",
          align: "end",
        },
        {
          text: "L.Projetado(%)",
          value: "projected_profit_percent",
          align: "end",
        },
        {
          text: "Preço médio",
          value: "average_price",
          align: "end",
          width: "120px",
        },
        { text: "Total", value: "total", align: "end" },
        {
          text: "ABC Vendas",
          value: "sale_abc_curve",
          align: "center",
          width: "120px",
        },
      ],

      columnOptions: [
        { text: "Código", value: "product_code" },
        { text: "Descrição", value: "product_name" },
        { text: "Marca", value: "brand_name" },
        { text: "Categoria", value: "category_name" },
        { text: "Quantidade", value: "quantity" },
        { text: "Lucro atual do produto", value: "variant_profit_percent" },
        { text: "Lucro(R$)", value: "profit_sum" },
        { text: "Lucro(%)", value: "profit_percent" },
        { text: "Lucro Projetado (R$)", value: "projected_profit" },
        { text: "Lucro Projetado (%)", value: "projected_profit_percent" },
        { text: "Preço médio", value: "average_price" },
        { text: "Total", value: "total" },
        { text: "ABC Vendas", value: "sale_abc_curve" },
      ],

      visibleColumns: [
        "product_code",
        "product_name",
        "quantity",
        "profit_sum",
        "average_price",
        "total",
        "profit_percent",
        "projected_profit",
        "projected_profit_percent",
      ],

      defaultColumns: [
        "product_code",
        "product_name",
        "quantity",
        "profit_sum",
        "average_price",
        "total",
        "profit_percent",
        "projected_profit",
        "projected_profit_percent",
      ],

      columnMenu: false,

      deliveryStatusData: [
        { value: null, text: "Todos" },
        { value: "no_delivery", text: "Sem Entrega" },
        { value: "delivery", text: "Entregue" },
      ],

      sales: [],

      showFilter: null,

      search: null,

      searchParams: {
        orderBy: "quantity",
        orderDirection: "desc",
        company_id: [],
        due_date_start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
        due_date_end: format(new Date(), "yyyy-MM-dd"),
        status: ["sale"],
        salesman_id: null,
        operation_id: null,
        delivery_type: null,
        text: null,
        category_id: null,
        brand_id: null,
      },

      isLoading: false,
    };
  },

  mounted() {
    this.select(1);
  },

  computed: {
    filteredHeaders() {
      return this.headers.filter((header) =>
        this.visibleColumns.includes(header.value)
      );
    },

    getVisibleColumnLabels() {
      return this.columnOptions
        .filter((col) => this.visibleColumns.includes(col.value))
        .map((col) => col.text);
    },

    grandTotal() {
      return this.sales.data && this.sales.data.length > 0
        ? this.sales.data[0].grand_total
        : 0;
    },

    grandProfit() {
      return this.sales.data && this.sales.data.length > 0
        ? this.sales.data[0].grand_profit
        : 0;
    },

    grandProjectedProfit() {
      return this.sales.data && this.sales.data.length > 0
        ? this.sales.data[0].grand_projected_profit
        : 0;
    },

    grandProjectedProfitPercent() {
      return (this.grandProjectedProfit / this.grandTotal) * 100 || 0;
    },

    grandProfitPercent() {
      return (this.grandProfit / this.grandTotal) * 100 || 0;
    },
  },

  methods: {
    async select(page = 1) {
      this.searchParams.page = page;
      this.isLoading = true;

      try {
        const response = await this.$http.index(
          "sale/sale-by-product-report",
          this.searchParams
        );
        this.sales = response;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    handleExport() {
      this.$http
        .download("sale/sale-by-product-report-export", this.searchParams)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Produtos_${this.searchParams.due_date_start}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          this.$loading.finish();
        })
        .catch((error) => {
          console.log(error);
          this.$loading.finish();
        });
    },

    setSalesman(salesman) {
      this.searchParams.salesman_id = salesman ? salesman.id : null;
      this.select(1);
    },

    handleRelationshipInput(relationship, field) {
      if (relationship) {
        this.searchParams[field] = relationship.id;
      } else {
        this.searchParams[field] = null;
      }

      this.select(1);
    },

    handleSearchInput(text) {
      this.searchParams.text = text ? text : null;

      this.search = null;

      this.searchParams.product_id = null;

      this.select(1);
    },

    handleClearFilter() {
      this.searchParams.category_id = null;
      this.searchParams.brand_id = null;
      this.searchParams.delivery_type = null;
      this.searchParams.operation_id = null;
      this.searchParams.salesman_id = null;
      this.select(1);
    },

    toggleColumn(columnValue) {
      const index = this.visibleColumns.indexOf(columnValue);
      if (index > -1) {
        this.visibleColumns.splice(index, 1);
      } else {
        this.visibleColumns.push(columnValue);
      }
    },

    selectAllColumns() {
      this.visibleColumns = this.columnOptions.map((col) => col.value);
    },

    clearAllColumns() {
      this.visibleColumns = [];
    },

    resetDefaultColumns() {
      this.visibleColumns = [...this.defaultColumns];
    },

    removeColumn(columnText) {
      const column = this.columnOptions.find((col) => col.text === columnText);
      if (column) {
        this.toggleColumn(column.value);
      }
    },

    setProduct() {
      this.searchParams.product_id = this.search.product_variants[0].product_id;
      this.searchParams.text = "";
      this.textSearchKey++; // força re-render do campo de busca
      this.select(1);
    },
  },
};
</script>
